import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Button, Spinner } from "@nike/epic-react-ui";
import { selectAccessToken, selectEmail } from "@nike/oauth-client";
import { requireLogin } from "../auth/LoginRequired";
import "./OwnershipApprovalPage.css";

const SLACKAPP_API =
  window.location.origin.includes("cloudred.nike.com") ||
  window.location.origin.includes("cloudred-prod.nikecloud.com")
    ? "https://1wpv0puuua.execute-api.us-west-2.amazonaws.com/prod"
    : "https://q44igx8ujh.execute-api.us-west-2.amazonaws.com/test";

const CLOUDRED_API = `${window.location.origin}/api`;

function formatApiError(body, status) {
  const detail = body && body.detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || JSON.stringify(d)).join("; ");
  }
  if (typeof detail === "string") {
    return detail;
  }
  return `HTTP ${status}`;
}

function statusBadgeClass(status) {
  const key = (status || "").toLowerCase();
  if (key === "pending") return "ownership-status-badge--pending";
  if (key === "awaiting-nominee") return "ownership-status-badge--approved";
  if (key === "approved") return "ownership-status-badge--approved";
  if (key === "completed") return "ownership-status-badge--completed";
  if (key === "rejected") return "ownership-status-badge--rejected";
  if (key === "failed") return "ownership-status-badge--failed";
  return "ownership-status-badge--expired";
}

const PageHero = ({ subtitle }) => (
  <header className="ownership-hero">
    <h1 className="ownership-hero-title">Ownership Change</h1>
    <p className="ownership-hero-subtitle">{subtitle}</p>
  </header>
);

const CenterState = ({ iconClass, iconChar, title, children }) => (
  <div className="ownership-page">
    <PageHero subtitle="CloudRed tagguid ownership transfer" />
    <div className="ownership-center-state">
      <div className={`ownership-outcome-icon ${iconClass}`}>{iconChar}</div>
      <h2>{title}</h2>
      {children}
    </div>
  </div>
);

const mapProps = state => ({
  accessToken: selectAccessToken(state),
  email: selectEmail(state)
});

const OwnershipApprovalPage = ({ accessToken, email, data }) => {
  const reqId = data && data.reqId;
  const token = (data && data.approvalToken) || "";

  const [request, setRequest] = useState(null);
  const [systemOwner, setSystemOwner] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!reqId || !token) {
      setError("Missing request id or token in URL.");
      return;
    }
    // The slackapp GET only validates the HMAC `t=` token from the URL — it
    // does NOT require the Okta Bearer. Fetch immediately on mount so the
    // page renders even before Okta hydrates the accessToken (avoids the
    // first-load "blank page until reload" symptom). Bearer is still sent
    // when available for defense-in-depth.
    const headers = { "Content-Type": "application/json" };
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    fetch(
      `${SLACKAPP_API}/ownership-request/${reqId}?t=${encodeURIComponent(
        token
      )}`,
      { headers }
    )
      .then(async res => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(formatApiError(body, res.status));
        }
        setRequest(body);
      })
      .catch(err => setError(err.message || String(err)));
  }, [reqId, token, accessToken]);

  useEffect(() => {
    if (!request || !accessToken || !request.tagguid) {
      return;
    }
    fetch(`${CLOUDRED_API}/ssot/systems/${request.tagguid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json"
      }
    })
      .then(async res => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok || body.status === "fail" || !body.owner) {
          return;
        }
        setSystemOwner(body.owner);
      })
      .catch(() => {});
  }, [request, accessToken]);

  const postSlackapp = async (suffix, extra = {}) => {
    const res = await fetch(
      `${SLACKAPP_API}/ownership-request/${reqId}/${suffix}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          t: token,
          okta_email: email,
          note: note || null,
          ...extra
        })
      }
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(formatApiError(body, res.status));
    }
    return body;
  };

  const submit = async decision => {
    if (!email) {
      setError(
        "Your Nike email is not available yet. Wait a moment, refresh, and try again."
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (decision === "reject") {
        const body = await postSlackapp("reject");
        setOutcome(body.status || "REJECTED");
        return;
      }
      const body = await postSlackapp("approve");
      setRequest(prev => ({ ...prev, status: body.status }));
      setOutcome(body.status || "COMPLETED");
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const retryTransfer = async () => {
    if (!email || !request) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = await postSlackapp("approve");
      setRequest(prev => ({ ...prev, status: body.status }));
      setOutcome(body.status || "COMPLETED");
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (error && !request) {
    return (
      <CenterState
        iconClass="ownership-outcome-icon--error"
        iconChar="!"
        title="Unable to load request"
      >
        <p>{error}</p>
      </CenterState>
    );
  }

  if (outcome && request) {
    const isSuccess = outcome === "COMPLETED";
    const isFailed = outcome === "FAILED";
    return (
      <CenterState
        iconClass={
          isSuccess
            ? "ownership-outcome-icon--success"
            : isFailed
            ? "ownership-outcome-icon--error"
            : "ownership-outcome-icon--neutral"
        }
        iconChar={isSuccess ? "✓" : isFailed ? "!" : "—"}
        title={
          isSuccess
            ? "Ownership transferred"
            : isFailed
            ? "Transfer failed"
            : `Request ${outcome.toLowerCase()}`
        }
      >
        {isSuccess && (
          <p>
            Tagguid <strong>{request.tagguid}</strong> is now owned by{" "}
            <strong>{request.new_owner_email}</strong>.
          </p>
        )}
        {isFailed && (
          <p>
            The approval was recorded but CloudRed could not update the owner.
            Please ping{" "}
            <a
              href="https://nikedigital.slack.com/archives/C8DRYHN9X"
              target="_blank"
              rel="noopener noreferrer"
            >
              #cloudred-discussion
            </a>{" "}
            if this persists.
          </p>
        )}
        {outcome === "REJECTED" && (
          <p>This ownership-change request was rejected.</p>
        )}
      </CenterState>
    );
  }

  if (!request) {
    return (
      <div className="ownership-page">
        <PageHero subtitle="Loading your approval request…" />
        <div className="ownership-center-state">
          <Spinner title="Loading approval request..." />
        </div>
      </div>
    );
  }

  if (submitting) {
    return (
      <div className="ownership-page">
        <PageHero subtitle="Applying ownership change in CloudRed" />
        <div className="ownership-center-state">
          <Spinner title="Updating owner..." />
          <h2>Updating owner in CloudRed…</h2>
          <p>This usually takes a few seconds.</p>
        </div>
      </div>
    );
  }

  const notYou =
    email &&
    request.approver_email &&
    email.toLowerCase() !== request.approver_email.toLowerCase();
  const expired = request.expires_at * 1000 < Date.now();
  const canAct = email && !notYou && !expired;
  const isPending = request.status === "PENDING";
  const isApproved = request.status === "APPROVED";
  const currentOwner = systemOwner || request.current_owner_email || "Unknown";

  return (
    <div className="ownership-page">
      <PageHero subtitle="Review the transfer details below and approve or reject this request." />

      <div className="ownership-shell">
        <div className="ownership-card">
          <div className="ownership-card-header">
            <span
              className={`ownership-status-badge ${statusBadgeClass(
                request.status
              )}`}
            >
              {expired && isPending ? "Expired" : request.status}
            </span>
            <span className="ownership-request-id">
              Request <code>{request.req_id}</code>
            </span>
          </div>

          <div className="ownership-card-body">
            {error && (
              <div className="ownership-alert ownership-alert--error">
                {error}
              </div>
            )}

            <p className="ownership-section-title">Proposed transfer</p>

            <div className="ownership-transfer">
              <div className="ownership-owner-box ownership-owner-box--from">
                <div className="ownership-owner-label">Current owner</div>
                <div className="ownership-owner-email">{currentOwner}</div>
              </div>
              <div className="ownership-arrow" aria-hidden="true">
                →
              </div>
              <div className="ownership-owner-box ownership-owner-box--to">
                <div className="ownership-owner-label">New owner</div>
                <div className="ownership-owner-email">
                  {request.new_owner_email}
                </div>
              </div>
            </div>

            <div className="ownership-tagguid">
              <div className="ownership-tagguid-label">Tagguid</div>
              <div className="ownership-tagguid-value">{request.tagguid}</div>
            </div>

            <p className="ownership-section-title">Request details</p>

            <div className="ownership-meta-grid">
              <div className="ownership-meta-item">
                <span className="ownership-meta-label">Requested by</span>
                <span className="ownership-meta-value">
                  {request.requester_email}
                </span>
              </div>
              <div className="ownership-meta-item">
                <span className="ownership-meta-label">Approver</span>
                <span className="ownership-meta-value">
                  {request.approver_email}
                </span>
              </div>
              <div className="ownership-meta-item">
                <span className="ownership-meta-label">Your sign-in</span>
                <span className="ownership-meta-value">
                  {email || "Loading…"}
                </span>
              </div>
              <div className="ownership-meta-item">
                <span className="ownership-meta-label">Expires</span>
                <span className="ownership-meta-value">
                  {new Date(request.expires_at * 1000).toLocaleString()}
                </span>
              </div>
            </div>

            {!email && (
              <div className="ownership-alert ownership-alert--warning">
                Loading your Nike identity…
              </div>
            )}

            {notYou && (
              <div className="ownership-alert ownership-alert--error">
                You are signed in as <strong>{email}</strong>, but this request
                is for <strong>{request.approver_email}</strong>. You cannot
                approve or reject it.
              </div>
            )}

            {expired && (
              <div className="ownership-alert ownership-alert--error">
                This request has expired and can no longer be resolved.
              </div>
            )}

            {isApproved && canAct && (
              <div className="ownership-alert ownership-alert--info">
                Owner update did not finish. Use <strong>Retry transfer</strong>{" "}
                to run the update again.
              </div>
            )}

            <div className="ownership-note-section">
              <label htmlFor="ownership-note">
                Note to requester (optional)
              </label>
              <textarea
                id="ownership-note"
                className="ownership-note-input"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Add a short note that will be shared with the requester…"
                disabled={!canAct || !isPending}
              />
            </div>

            <div className="ownership-actions">
              {isPending && (
                <>
                  <Button
                    className="ownership-btn-approve"
                    onClick={() => submit("approve")}
                    disabled={!canAct}
                  >
                    Approve transfer
                  </Button>
                  <Button
                    className="ownership-btn-reject"
                    onClick={() => submit("reject")}
                    disabled={!canAct}
                  >
                    Reject
                  </Button>
                </>
              )}
              {isApproved && (
                <Button
                  className="ownership-btn-retry"
                  onClick={() => retryTransfer()}
                  disabled={!canAct}
                >
                  Retry transfer
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(mapProps)(
  requireLogin("Ownership approval")(OwnershipApprovalPage)
);
